/**
 * Mobile App Screens API Contract
 *
 * GET /v1/schools/:school_id/mobile-app-screens
 *
 * A READ-ONLY projection of a school's `MobileApp` registry row, assembled for
 * the "Custom App Screens" tab in Manage App → Design: the fixed pre-login
 * screens (login, onboarding carousel, splash) and the home-screen icon, as
 * they are configured to ship today.
 *
 * Why a school-scoped endpoint rather than reusing the mobile-apps ones: every
 * `/v1/mobile-apps/*` read is super-admin gated (a fleet surface), and the
 * public by-`schoolAppId` manifest is keyed on a build identifier the web
 * dashboard has no reason to know. This endpoint answers "what does MY school's
 * app look like" for a per-school admin, and returns nothing that is not
 * already rendered on the school's own app.
 *
 * Deliberately NOT included: OneSignal keys, ASC/Play credentials, Datadog
 * tokens, or any other secret on the registry row. The projection is an
 * explicit allowlist of presentation fields, not a filtered row.
 */

import { z } from 'zod'
import { MobileAppBrandingConfigSchema } from '../../schemas/entities/mobile-app-branding'
import { MobileAppOnboardingConfigSchema } from '../../schemas/entities/mobile-app-onboarding'

/**
 * One resolved image, ready to render. `url` is a Cloudinary delivery URL built
 * for a BROWSER preview (see `buildAssetPreviewUrl` backend-side) — it is not
 * the untransformed build-slot URL the EAS staging script consumes.
 */
export const MobileAppScreenAssetSchema = z.object({
  url: z.string().url(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable()
})
export type MobileAppScreenAsset = z.infer<typeof MobileAppScreenAssetSchema>

/**
 * Splash resize behavior, mirroring `MobileApp.splashResizeMode` and the
 * `expo-splash-screen` config in `apps/mobile/app.config.ts`. Only iOS honors
 * it — Android 12+ always renders an icon-shaped splash masked into a system
 * circle, whatever this says.
 */
export const MobileAppSplashResizeModeSchema = z.enum(['contain', 'cover'])
export type MobileAppSplashResizeMode = z.infer<typeof MobileAppSplashResizeModeSchema>

export const MobileAppScreensRegistrySchema = z.object({
  /** MobileApp publicId. */
  id: z.string(),
  /**
   * The ON-DEVICE label — what appears under the home-screen icon. Often a
   * product brand rather than the school's name ("My CountryDay"), which is
   * exactly why the icon preview renders this and not the school name.
   */
  name: z.string().nullable(),
  /** The store LISTING title. Never rendered on-device; shown as metadata. */
  displayName: z.string().nullable(),
  bundleId: z.string().nullable(),
  androidPackage: z.string().nullable(),
  splashBackgroundColor: z.string().nullable(),
  splashResizeMode: MobileAppSplashResizeModeSchema.nullable(),
  isActive: z.boolean()
})
export type MobileAppScreensRegistry = z.infer<typeof MobileAppScreensRegistrySchema>

export const MobileAppScreensDataSchema = z.object({
  /**
   * `null` when the school has no `MobileApp` row at all — it rides the shared
   * School Journey app. The tab renders an explicit empty state for this rather
   * than an empty phone, since a blank preview reads as "misconfigured".
   */
  app: MobileAppScreensRegistrySchema.nullable(),
  /** The label to render under the icon: `app.name` falling back to the school name. */
  appLabel: z.string(),
  branding: MobileAppBrandingConfigSchema.nullable(),
  onboarding: MobileAppOnboardingConfigSchema.nullable(),
  /**
   * Committed asset slots, keyed by canonical slot name (`app-icon`, `splash`,
   * `adaptive-icon-foreground`, `onboard-1..3`, `login-bg`, the logo slots).
   * Slots with nothing committed are ABSENT — the preview renders its own
   * placeholder, so an empty key would be indistinguishable from a broken URL.
   */
  assets: z.record(z.string(), MobileAppScreenAssetSchema)
})
export type MobileAppScreensData = z.infer<typeof MobileAppScreensDataSchema>

export const GetMobileAppScreensResponseSchema = z.object({
  data: MobileAppScreensDataSchema,
  message: z.string()
})
export type GetMobileAppScreensResponse = z.infer<typeof GetMobileAppScreensResponseSchema>
